import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { PageHeader } from "../components/ui/kaizen";

const TEXT_FIELDS = [
  ["institute_name", "Nom de l'institut"], ["system_name", "Nom du système"], ["network_name", "Nom du réseau"],
  ["event_name", "Nom public officiel de l'événement"], ["event_title", "Titre principal (affiches)"], ["event_subtitle", "Sous-titre (affiches)"],
  ["event_signature", "Signature officielle"], ["event_location", "Lieu officiel"], ["event_address", "Adresse complète (affiches)"],
  ["event_date", "Date (AAAA-MM-JJ)"], ["event_date_label", "Date affichée"], ["event_city", "Ville"],
  ["event_tagline", "Accroche courte"], ["event_tagline_sub", "Sous-texte court"],
  ["trial_service_name", "Nom du service d'essai"], ["trial_price", "Prix officiel du cours d'essai ($)"], ["trial_description", "Description officielle du cours d'essai"],
  ["phone", "Téléphone"], ["email", "Courriel"], ["booking_url", "Lien de réservation (cours d'essai)"], ["registration_url", "Lien d'inscription (événement)"], ["rules_url", "Lien du règlement"],
  ["missing_info_text", "Texte si information manquante"],
];
const AREA_FIELDS = [
  ["event_positioning", "Positionnement officiel (texte long)"], ["safety_message", "Règlement général officiel (sécurité)"], ["bjj_public_text", "Formulation publique Jiu-Jitsu brésilien"],
  ["rules_format_note", "Note « format interne »"], ["mto_note", "Note référentiel MTO / IFMA"], ["trial_sms_confirm", "SMS de confirmation (cours d'essai)"], ["trial_sms_reminder", "SMS de rappel J-1"],
  ["trial_email_subject", "Objet courriel de confirmation"], ["trial_email_body", "Corps courriel de confirmation"],
  ["schedule_reply_rule", "Règle IA « Quels sont vos horaires ? » (qualification avant tout horaire — Camille, Messenger, réponses auto)"], ["schedule_reply_template", "Modèle de réponse « horaires » (copier-coller, {prenom})"],
  ["weigh_in_note", "Note pesée officielle (jour J)"],
];
const LIST_FIELDS = [["mto_experience_classes", "MTO — classes d'expérience (1 par ligne)"], ["mto_fouls", "MTO — fautes interdites (1 par ligne)"], ["mto_medical", "MTO — exigences médicales (1 par ligne)"], ["mto_progression", "MTO — logique de progression interne (1 par ligne)"], ["scoring_rules_striking", "Règlement Kickboxing/Muaythai (1 règle par ligne)"], ["scoring_rules_bjj", "Règlement Jiu-Jitsu brésilien (1 par ligne)"], ["forbidden_terms", "Termes interdits (1 par ligne)"], ["allowed_terms", "Termes autorisés (1 par ligne)"]];
const DISC_IDS = ["muay_thai_educatif", "jjb", "can_ryu_demo", "petits_samourais"];

export default function RegistreMaitreModule() {
  const [terms, setTerms] = useState(null);
  const [form, setForm] = useState({});
  const [tab, setTab] = useState("registre");
  const [audit, setAudit] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/official-terms");
    setTerms(data.terms); setForm(data.terms);
  };
  useEffect(() => { load().catch(() => toast.error("Chargement impossible")); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const patch = { ...form, trial_price: Number(form.trial_price) };
      const { data } = await api.put("/official-terms", { patch });
      setTerms(data.terms); setForm(data.terms); toast.success("Registre maître enregistré — appliqué à toutes les pages, agents IA, SMS et affiches.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); } finally { setBusy(false); }
  };
  const loadAudit = async () => {
    setBusy(true);
    try { const { data } = await api.get("/official-terms/audit"); setAudit(data); } catch { toast.error("Audit impossible"); } finally { setBusy(false); }
  };
  const fixDb = async () => {
    if (!window.confirm("Corriger maintenant les contenus vivants de la base (modèles, campagnes, événements) ? Chaque changement est journalisé avec l'ancienne valeur.")) return;
    setBusy(true);
    try { const { data } = await api.post("/official-terms/audit/fix?confirm=true"); toast.success(`Corrigé : ${JSON.stringify(data.fixed)}`); await loadAudit(); } catch { toast.error("Erreur"); } finally { setBusy(false); }
  };
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const inputCls = "w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm text-zinc-100";

  if (!terms) return <div className="p-8 text-zinc-500">Chargement…</div>;
  return (
    <div className="space-y-6" data-testid="registre-maitre-module">
      <PageHeader title="Registre maître — Contenu officiel"
        subtitle="Toutes les pages, agents IA, affiches, SMS, courriels et QR codes lisent ces valeurs. Modifiable sans code." />
      <div className="flex gap-2">
        {[["registre", "📘 Registre"], ["audit", "🔎 Audit global — Harmonisation du contenu"]].map(([k, l]) => (
          <button key={k} onClick={() => { setTab(k); if (k === "audit" && !audit) loadAudit(); }} data-testid={`rm-tab-${k}`}
            className={`px-4 py-2 rounded text-sm font-semibold ${tab === k ? "bg-amber-500 text-black" : "bg-zinc-900 text-zinc-300"}`}>{l}</button>
        ))}
      </div>

      {tab === "registre" && (
        <div className="space-y-6">
          <div className="grid md:grid-cols-2 gap-3">
            {TEXT_FIELDS.map(([k, l]) => (
              <div key={k}><label className="text-xs text-zinc-400">{l}</label>
                <input value={form[k] ?? ""} onChange={(e) => setF(k, e.target.value)} className={inputCls} data-testid={`rm-field-${k}`} /></div>
            ))}
          </div>
          <div className="rounded-xl border border-zinc-800 p-4">
            <div className="text-xs tracking-[0.3em] text-amber-400 mb-3">LIBELLÉS DES DISCIPLINES (les identifiants techniques ne changent jamais)</div>
            <div className="grid md:grid-cols-2 gap-3">
              {DISC_IDS.map((id) => (
                <div key={id}><label className="text-xs text-zinc-500 font-mono">{id}</label>
                  <input value={form.disciplines?.[id] ?? ""} onChange={(e) => setF("disciplines", { ...form.disciplines, [id]: e.target.value })} className={inputCls} data-testid={`rm-disc-${id}`} /></div>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-3 rounded-xl border border-red-500/30 bg-red-500/5 p-4" data-testid="rm-free-trial">
            <input type="checkbox" checked={!!form.free_trial_enabled} onChange={(e) => setF("free_trial_enabled", e.target.checked)} id="ft" />
            <label htmlFor="ft" className="text-sm text-zinc-200">Essai sans frais autorisé — <b>{form.free_trial_enabled ? "OUI" : "NON"}</b> (règle actuelle : NON — toute mention « gratuit » est remplacée par « {form.trial_label} »)</label>
          </div>
          {AREA_FIELDS.map(([k, l]) => (
            <div key={k}><label className="text-xs text-zinc-400">{l}</label>
              <textarea rows={k.includes("body") || k.includes("positioning") || k.includes("safety") || k.includes("schedule_reply") ? 7 : 3} value={form[k] ?? ""} onChange={(e) => setF(k, e.target.value)} className={inputCls} data-testid={`rm-area-${k}`} /></div>
          ))}
          <div className="grid md:grid-cols-2 gap-3">
            {LIST_FIELDS.map(([k, l]) => (
              <div key={k}><label className="text-xs text-zinc-400">{l}</label>
                <textarea rows={7} value={(form[k] || []).join("\n")} onChange={(e) => setF(k, e.target.value.split("\n").map((x) => x.trim()).filter(Boolean))} className={inputCls} data-testid={`rm-list-${k}`} /></div>
            ))}
          </div>
          <div className="rounded-xl border border-zinc-800 p-4" data-testid="rm-mto-table">
            <div className="text-xs tracking-[0.3em] text-amber-400 mb-2">TABLEAU MAÎTRE MTO 2024 · IFMA ({(form.mto_master_table || []).length} lignes) — modifiable ligne par ligne</div>
            <div className="overflow-x-auto"><table className="w-full text-xs"><thead><tr className="text-zinc-500 text-left">{["Âge", "Classe", "Frappes tête", "Genoux tête", "Coudes tête", "Format", "Notes"].map((h) => <th key={h} className="py-1 pr-2">{h}</th>)}</tr></thead>
              <tbody>{(form.mto_master_table || []).map((r, i) => (
                <tr key={i}>{["age", "classe", "tete", "genoux_tete", "coudes_tete", "format", "notes"].map((k) => (
                  <td key={k} className="pr-2 py-0.5"><input value={r[k] ?? ""} onChange={(e) => { const t = [...form.mto_master_table]; t[i] = { ...t[i], [k]: e.target.value }; setF("mto_master_table", t); }} className={`${inputCls} py-1 ${k === "notes" ? "min-w-[260px]" : "min-w-[90px]"}`} data-testid={`rm-mto-${i}-${k}`} /></td>
                ))}</tr>
              ))}</tbody></table></div>
          </div>
          <div className="flex gap-3">
            <button onClick={save} disabled={busy} className="px-5 py-2.5 rounded bg-amber-500 text-black font-bold text-sm" data-testid="rm-save-btn">💾 Enregistrer le registre</button>
            <a href="/reglement-securite" target="_blank" rel="noreferrer" className="px-5 py-2.5 rounded border border-zinc-700 text-sm text-zinc-200" data-testid="rm-rules-page-link">Voir la page publique du règlement</a>
          </div>
        </div>
      )}

      {tab === "audit" && (
        <div className="space-y-5" data-testid="rm-audit">
          <div className="flex gap-3">
            <button onClick={loadAudit} disabled={busy} className="px-4 py-2 rounded bg-zinc-800 text-sm" data-testid="rm-audit-refresh">↻ Relancer l'audit</button>
            <button onClick={fixDb} disabled={busy} className="px-4 py-2 rounded bg-emerald-600 text-white text-sm" data-testid="rm-audit-fix">🧹 Corriger les contenus vivants (base)</button>
          </div>
          {!audit ? <div className="text-zinc-500 text-sm">Analyse en cours…</div> : (
            <>
              <div className="grid md:grid-cols-3 gap-3">
                <Stat label="Occurrences restantes dans le code" value={audit.code.total} ok={audit.code.total === 0} testid="rm-stat-code" />
                <Stat label="Occurrences restantes en base (contenus vivants)" value={audit.database.total} ok={audit.database.total === 0} testid="rm-stat-db" />
                <Stat label="Changements journalisés" value={audit.changes_total} ok testid="rm-stat-changes" />
              </div>
              <HitList title="Code (fichiers)" rows={audit.code.hits.map((h) => `${h.file}:${h.line} — ${h.term} — ${h.excerpt}`)} testid="rm-hits-code" />
              <HitList title="Base de données (contenus vivants)" rows={audit.database.hits.map((h) => `${h.collection} · ${h.field} — ${h.terms.join(", ")} — ${h.excerpt}`)} testid="rm-hits-db" />
              <div className="rounded-xl border border-zinc-800 p-4">
                <div className="text-xs tracking-[0.3em] text-amber-400 mb-2">JOURNAL DES CHANGEMENTS (ancienne → nouvelle valeur)</div>
                <div className="max-h-96 overflow-auto text-xs space-y-2" data-testid="rm-changes">
                  {audit.changes.map((c, i) => (
                    <div key={i} className="border-b border-zinc-900 pb-2">
                      <div className="text-zinc-500">{c.at?.slice(0, 16).replace("T", " ")} · {c.by} · {c.where || c.collection}{c.field ? ` · ${c.field}` : ""}</div>
                      <div className="text-red-300/80 line-through">{String(c.old ?? "").slice(0, 160)}</div>
                      <div className="text-emerald-300">{String(c.new ?? "").slice(0, 160)}</div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

const Stat = ({ label, value, ok, testid }) => (
  <div className={`rounded-xl border p-4 ${ok ? "border-emerald-500/30 bg-emerald-500/5" : "border-red-500/30 bg-red-500/5"}`} data-testid={testid}>
    <div className="text-3xl font-bold">{value}</div><div className="text-xs text-zinc-400 mt-1">{label}</div>
  </div>
);
const HitList = ({ title, rows, testid }) => (
  <div className="rounded-xl border border-zinc-800 p-4" data-testid={testid}>
    <div className="text-xs tracking-[0.3em] text-amber-400 mb-2">{title} — {rows.length}</div>
    {rows.length === 0 ? <div className="text-emerald-300 text-sm">✅ Aucune occurrence</div> : (
      <div className="max-h-72 overflow-auto text-xs font-mono space-y-1 text-zinc-300">{rows.map((r, i) => <div key={i}>{r}</div>)}</div>
    )}
  </div>
);
